import {
  DomainEntity,
  EntityStatus,
  hasEntityOperation,
} from '@web-config-app/core';

const environmentSyncDisabledTooltipMap = {
  unsavedChanges: 'SYNC_ENVIRONMENT_UNSAVED_CHANGES',
  draftToPublishNotAllowed: 'SYNC_ENVIRONMENT_DRAFT_TO_PUBLISH_NOT_ALLOWED',
  notAllowedStatus: 'SYNC_ENVIRONMENT_NOT_ALLOWED_STATUS',
  notAllowedRepublish: 'SYNC_ENVIRONMENT_NOT_ALLOWED_REPUBLISH',
  notAllowedReadOnly: 'SYNC_ENVIRONMENT_NOT_ALLOWED_READ_ONLY',
} as const;

export type EnvironmentSyncDisabledReasonKey =
  | (typeof environmentSyncDisabledTooltipMap)[keyof typeof environmentSyncDisabledTooltipMap];

interface GetEnvironmentSyncDisabledTooltipParams {
  isEntitySaved: boolean;
  status: EntityStatus;
  otherStatus?: EntityStatus;
  domainEntity: DomainEntity;
  otherReadOnly?: boolean;
}

// Figures out why we can't sync between environments and returns the correct tooltip key for the disabled sync button.
export const getEnvironmentSyncDisabledTooltip = ({
  isEntitySaved,
  status,
  otherStatus,
  domainEntity,
  otherReadOnly,
}: GetEnvironmentSyncDisabledTooltipParams):
  | EnvironmentSyncDisabledReasonKey
  | undefined => {
  if (!isEntitySaved) {
    return environmentSyncDisabledTooltipMap.unsavedChanges;
  }

  if (status === 'draft' && otherStatus === 'published') {
    return environmentSyncDisabledTooltipMap.draftToPublishNotAllowed;
  }

  if (
    status === 'published' &&
    otherStatus === 'published' &&
    !hasEntityOperation(domainEntity, 'republish')
  ) {
    return environmentSyncDisabledTooltipMap.notAllowedRepublish;
  }

  if (otherReadOnly) {
    return environmentSyncDisabledTooltipMap.notAllowedReadOnly;
  }

  if (otherStatus) {
    const sourceEnvironmentSyncAllowed =
      status === 'draft' || status === 'published';
    const targetEnvironmentSyncAllowed =
      otherStatus === 'draft' || otherStatus === 'published';
    if (!sourceEnvironmentSyncAllowed || !targetEnvironmentSyncAllowed) {
      return environmentSyncDisabledTooltipMap.notAllowedStatus;
    }
  }

  return undefined;
};
